import type { MuscleId } from './muscles'

export interface MachineExercise {
  id: string
  name: string
  /** Exact QR payload as printed on the machine */
  qrPayload: string
  muscles: MuscleId[]
  defaultSets: number
  defaultReps: number
  notes?: string
}

/**
 * Gym machine QR codes → muscle targets.
 * QR payloads are Vimeo HLS URLs printed on the machines.
 */
export const MACHINE_CATALOG: MachineExercise[] = [
  {
    id: 'leg-extension',
    name: 'Leg Extension',
    qrPayload:
      'https://player.vimeo.com/external/358292331.m3u8?s=9bde6dae9a7b6aa042ea10dec9a3580c9ca51304',
    muscles: [
      'rectus_femoris_l',
      'rectus_femoris_r',
      'vastus_lateralis_l',
      'vastus_lateralis_r',
      'vastus_medialis_l',
      'vastus_medialis_r',
      'vastus_intermedius_l',
      'vastus_intermedius_r',
    ],
    defaultSets: 3,
    defaultReps: 12,
    notes: 'Primary: quadriceps (rectus femoris + vasti)',
  },
  {
    id: 'hip-abduction',
    name: 'Hip Abduction',
    qrPayload:
      'https://player.vimeo.com/external/359137886.m3u8?s=0a587d52b0c6a679c8ad851e828e68c4920a4455',
    muscles: [
      'gluteus_medius_l',
      'gluteus_medius_r',
      'gluteus_minimus_l',
      'gluteus_minimus_r',
      'tfl_l',
      'tfl_r',
    ],
    defaultSets: 3,
    defaultReps: 12,
    notes: 'Primary: gluteus medius/minimus + TFL',
  },
]

const byId = new Map(MACHINE_CATALOG.map((m) => [m.id, m]))

export function normalizeQr(raw: string): string {
  return raw.trim()
}

function payloadMatches(raw: string, qrPayload: string): boolean {
  const n = normalizeQr(raw)
  const payload = normalizeQr(qrPayload)
  if (n === payload) return true
  try {
    const u = new URL(n)
    const key = `${u.origin}${u.pathname}${u.search}`
    if (key === payload) return true
  } catch {
    /* non-URL scans fall through */
  }
  return n.includes(payload) || payload.includes(n)
}

/**
 * All catalog workouts for a QR. Same sticker may map to more than one config
 * (one machine, two workouts) - callers should prompt when length > 1.
 */
export function machinesMatchingQr(
  catalog: MachineExercise[],
  raw: string,
): MachineExercise[] {
  const n = normalizeQr(raw)
  const exact = catalog.filter((m) => normalizeQr(m.qrPayload) === n)
  if (exact.length) return exact

  try {
    void new URL(n)
    return catalog.filter((m) => payloadMatches(n, m.qrPayload))
  } catch {
    return catalog.filter((m) => n.includes(m.id))
  }
}

export function findMachinesByQr(raw: string): MachineExercise[] {
  return machinesMatchingQr(MACHINE_CATALOG, raw)
}

/** First catalog match for a QR (prefer findMachinesByQr when duplicates matter). */
export function findMachineByQr(raw: string): MachineExercise | undefined {
  return findMachinesByQr(raw)[0]
}

export function getMachine(id: string): MachineExercise | undefined {
  return byId.get(id)
}

/** Manual exercise presets (no QR) */
export const MANUAL_PRESETS: { id: string; name: string; muscles: MuscleId[] }[] = [
  {
    id: 'manual-quads',
    name: 'Quad-focused (squat / extension)',
    muscles: [
      'rectus_femoris_l',
      'rectus_femoris_r',
      'vastus_lateralis_l',
      'vastus_lateralis_r',
      'vastus_medialis_l',
      'vastus_medialis_r',
      'vastus_intermedius_l',
      'vastus_intermedius_r',
    ],
  },
  {
    id: 'manual-hip-abd',
    name: 'Hip abduction / outer glute',
    muscles: [
      'gluteus_medius_l',
      'gluteus_medius_r',
      'gluteus_minimus_l',
      'gluteus_minimus_r',
      'tfl_l',
      'tfl_r',
    ],
  },
  {
    id: 'manual-push',
    name: 'Push day (chest / delts / tris)',
    muscles: ['chest', 'delts_l', 'delts_r', 'triceps_l', 'triceps_r', 'traps_l', 'traps_r'],
  },
  {
    id: 'manual-pull',
    name: 'Pull day (lats / bis)',
    muscles: ['lats_l', 'lats_r', 'biceps_l', 'biceps_r', 'forearms_l', 'forearms_r', 'lower_back'],
  },
  {
    id: 'manual-legs',
    name: 'Leg day (quads / hams / glutes)',
    muscles: [
      'rectus_femoris_l',
      'rectus_femoris_r',
      'vastus_lateralis_l',
      'vastus_lateralis_r',
      'vastus_medialis_l',
      'vastus_medialis_r',
      'vastus_intermedius_l',
      'vastus_intermedius_r',
      'hamstrings_l',
      'hamstrings_r',
      'gluteus_maximus_l',
      'gluteus_maximus_r',
      'adductors_l',
      'adductors_r',
      'calves_l',
      'calves_r',
    ],
  },
]
